import { createSlice } from '@reduxjs/toolkit';
import { OCR_EXTRACTION_STATUS } from 'utils/ocrVerification';
import { formControlActions } from 'store/formControlSlice/formControlSlice';

const initialState = {
    // { [transactionId]: attempt[] } in request order; an attempt is never removed while its form is open (audit)
    attemptsByTransactionId: {},
};

const ocrVerificationSlice = createSlice({
    name: 'ocrVerification',
    initialState,
    reducers: {
        // The camera captured an OCR snapshot. Ignored while the same identifier already has an attempt in flight, so
        // a double click never sends two requests.
        extractionRequested(state, { payload: { transactionId, attemptId, identifier, snapshotId, cameraName, cameraType, requestedAt } }) {
            const attempts = state.attemptsByTransactionId[transactionId] ?? (state.attemptsByTransactionId[transactionId] = []);
            if (attempts.some((attempt) => attempt.identifier === identifier && attempt.status === OCR_EXTRACTION_STATUS.PENDING)) return;
            attempts.push({
                id: attemptId,
                identifier,
                status: OCR_EXTRACTION_STATUS.PENDING,
                value: null,
                snapshotId,
                cameraName: cameraName ?? null,
                cameraType: cameraType ?? null,
                assetId: null,
                condorRequestId: null,
                message: null,
                requestedAt,
                completedAt: null,
            });
        },

        // Only a pending attempt completes: a response that arrives after the attempt timed out is dropped.
        extractionCompleted(
            state,
            { payload: { transactionId, attemptId, status, value = null, assetId = null, condorRequestId = null, message = null, completedAt } }
        ) {
            const attempt = state.attemptsByTransactionId[transactionId]?.find(({ id }) => id === attemptId);
            if (!attempt || attempt.status !== OCR_EXTRACTION_STATUS.PENDING) return;
            Object.assign(attempt, { status, value, assetId: assetId ?? attempt.assetId, condorRequestId, message, completedAt });
        },

        // The transaction's panel closed (submit, discard, drop), so a reopen verifies again from scratch.
        transactionClosed(state, { payload: transactionId }) {
            delete state.attemptsByTransactionId[transactionId];
        },

        allTransactionsClosed(state) {
            state.attemptsByTransactionId = {};
        },
    },
    extraReducers: (builder) => {
        builder.addCase(formControlActions.removeForm, (state, { payload: transactionId }) => {
            delete state.attemptsByTransactionId[transactionId];
        });
    },
});

export const ocrVerificationActions = ocrVerificationSlice.actions;
export const ocrVerificationReducer = ocrVerificationSlice.reducer;
